import React, { useEffect } from 'react';
import { IconRepeat } from '../common/Icons';
import { useI18n } from '../../services/i18n';
import { fmtDate, fmtTime, money, until } from '../../services/api';

export default function OrderList({
  orders,
  ordersTab,
  setOrdersTab,
  loadOrders,
  onCancelOrder,
  onRepeatOrder,
  onNavigate
}) {
  const { t, lang } = useI18n();

  useEffect(() => {
    loadOrders(ordersTab);
  }, [ordersTab, loadOrders]);

  const renderOrderCard = (o) => {
    const start = new Date(o.start);
    const end = new Date(start.getTime() + (o.hours || 1) * 3600e3);
    const now = Date.now();
    const meta = `${fmtDate(o.start, lang)} · ${fmtTime(start)} – ${fmtTime(end)} · ${o.hours} ${t('hours_unit')} · $${o.cost}`;

    const isRunning = o.status === 'running' || (o.status === 'scheduled' && now >= start.getTime() && now < end.getTime());

    if (isRunning) {
      const pct = Math.min(100, Math.max(0, Math.round(((now - start.getTime()) / (end.getTime() - start.getTime())) * 100)));
      return (
        <article className="card" key={o.id} data-testid={`order-card-${o.id}`}>
          <div className="row">
            <b style={{ fontSize: '17px' }}>{o.model}</b>
            <span className="status status--running">
              <i />{t('status_running')}
            </span>
          </div>
          <div className="small muted" style={{ fontSize: '14px' }}>{meta}</div>
          <div className="col" style={{ gap: '6px' }}>
            <div className="progress">
              <div style={{ width: `${pct}%` }} />
            </div>
            <span className="small muted">{t('left_label')} {until(end.getTime() - now, lang)}</span>
          </div>
        </article>
      );
    }

    if (o.status === 'scheduled') {
      const onlineAt = new Date(start.getTime() - 15 * 60e3);
      return (
        <article className="card" key={o.id} data-testid={`order-card-${o.id}`}>
          <div className="row">
            <b style={{ fontSize: '17px' }}>{o.model}</b>
            <span className="status status--scheduled">
              <i />{t('status_scheduled')}
            </span>
          </div>
          <div className="small muted" style={{ fontSize: '14px' }}>{meta}</div>
          <div className="small" style={{ color: 'var(--text-2)' }}>
            {t('starts_in')} {until(start.getTime() - now, lang)} · {t('online_from')} {fmtTime(onlineAt)}
            {o.remind ? ` · ${t('remind_tag')}` : ''}
          </div>
          <div className="grid2">
            <button
              className="btn btn--ghost-danger"
              onClick={() => onCancelOrder(o.id)}
              data-testid={`cancel-order-${o.id}`}
            >
              {t('btn_cancel')}
            </button>
            <button
              className="btn btn--secondary btn--small"
              onClick={() => onRepeatOrder(o)}
              data-testid={`repeat-order-${o.id}`}
            >
              {t('btn_repeat')}
            </button>
          </div>
        </article>
      );
    }

    const isCancelled = o.status === 'cancelled';
    const label = isCancelled ? t('status_cancelled') : t('status_completed');
    const sub = isCancelled
      ? `${fmtDate(o.start, lang)} · ${fmtTime(start)} – ${fmtTime(end)} · ${money(o.refunded ?? o.cost)} ${t('refunded_label')}`
      : meta;

    return (
      <article
        className={`card ${isCancelled ? 'card--dim' : ''}`}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 16px'
        }}
        key={o.id}
        data-testid={`order-card-${o.id}`}
      >
        <div className="col" style={{ gap: '4px' }}>
          <b style={{ fontSize: '16px' }}>{o.model}</b>
          <span className="small muted">{sub}</span>
          <span className={`status status--${o.status}`} style={{ height: '22px', width: 'fit-content' }}>
            {label}
          </span>
        </div>
        <button
          className="pill"
          style={{ height: '40px', fontSize: '14px', flex: 'none' }}
          onClick={() => onRepeatOrder(o)}
          data-testid={`repeat-order-${o.id}`}
        >
          <IconRepeat />
          {t('btn_repeat')}
        </button>
      </article>
    );
  };

  return (
    <main className="screen" data-testid="screen-orders">
      <h1>{t('orders_title')}</h1>

      <div className="seg" role="tablist">
        <button
          className={ordersTab === 'upcoming' ? 'is-on' : ''}
          onClick={() => setOrdersTab('upcoming')}
          data-testid="tab-upcoming"
        >
          {t('tab_upcoming')}
        </button>
        <button
          className={ordersTab === 'history' ? 'is-on' : ''}
          onClick={() => setOrdersTab('history')}
          data-testid="tab-history"
        >
          {t('tab_history')}
        </button>
      </div>

      <div className="col" style={{ gap: '12px' }}>
        {orders === null ? (
          <div className="skeleton" data-testid="orders-loading" />
        ) : orders.length > 0 ? (
          orders.map(renderOrderCard)
        ) : (
          <div className="empty" data-testid="orders-empty">
            <b>{ordersTab === 'upcoming' ? t('empty_upcoming') : t('empty_history')}</b>
            <span className="small muted">{t('empty_orders_sub')}</span>
          </div>
        )}

        <button
          className="btn btn--secondary"
          style={{ border: '1px dashed var(--border-2)', background: 'transparent' }}
          onClick={() => onNavigate('order')}
          data-testid="btn-new-order-from-list"
        >
          {t('btn_new_order')}
        </button>
      </div>
    </main>
  );
}
